import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { TrendingUp, ShieldOff, Loader2, XCircle, CheckCircle } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const fmt$ = v => `$${Math.round(v || 0).toLocaleString()}`;

export function ChasePerformancePanel({ refreshKey }) {
  const api = useApi();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/payments/chase-performance').then(r => setData(r.data)).catch(() => {});
  }, [api, refreshKey]);

  if (!data) return null;
  const kpis = [
    { label: 'Reminders sent', value: data.reminders_sent, color: C.blue },
    { label: 'Invoices chased', value: data.invoices_reminded, color: C.navy },
    { label: 'Converted to payment', value: data.converted, color: C.green },
    { label: 'Recovery rate', value: `${data.conversion_rate}%`, color: data.conversion_rate > 30 ? C.green : C.amber },
    { label: 'Recovered', value: fmt$(data.recovered), color: C.green },
    { label: 'Pending cancellations', value: data.pending_cancellations, color: data.pending_cancellations ? C.red : C.slate },
  ];
  return (
    <Card className="p-4 rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.2)' }} data-testid="chase-performance-panel">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>
        <TrendingUp className="w-3.5 h-3.5" style={{ color: C.blue }} />Chase Performance
      </h3>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-3">
        {kpis.map(k => (
          <div key={k.label} data-testid={`chase-kpi-${k.label.toLowerCase().replace(/ /g, '-')}`}>
            <p className="text-lg font-semibold font-mono" style={{ color: k.color }}>{k.value}</p>
            <p className="text-[9px] uppercase tracking-wider" style={{ color: C.slate }}>{k.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {(data.per_stage || []).map(s => (
          <div key={s.stage} className="p-2 rounded-sm border text-[10px]" style={{ borderColor: 'rgba(136,150,168,0.15)' }}
            data-testid={`chase-stage-${s.stage}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold" style={{ color: C.navy }}>{s.label}</span>
              <span style={{ color: C.slate }}>{s.sent} sent</span>
            </div>
            <div className="flex items-center justify-between mt-0.5" style={{ color: C.slate }}>
              <span>{s.converted} paid ({s.conversion_rate}%)</span>
              <span className="font-mono" style={{ color: C.green }}>{fmt$(s.recovered)}</span>
            </div>
          </div>
        ))}
      </div>
      {data.policies_cancelled > 0 && (
        <p className="text-[10px] mt-2" style={{ color: C.slate }}>
          {data.policies_cancelled} polic{data.policies_cancelled === 1 ? 'y' : 'ies'} cancelled for non-payment · {data.queue_resolved_by_payment} queue entr{data.queue_resolved_by_payment === 1 ? 'y' : 'ies'} resolved by payment
        </p>
      )}
    </Card>
  );
}

export function CancellationQueue({ onChanged }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(() => {
    api.get('/payments/cancellations').then(r => setData(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const act = async (queueId, action, invoiceNumber) => {
    if (action === 'approve' && !window.confirm(`Cancel the policy behind ${invoiceNumber} for non-payment? A cancellation notice will be emailed to the broker.`)) return;
    setActingId(queueId);
    try {
      await api.post(`/payments/cancellations/${queueId}/${action}`, action === 'dismiss' ? { reason: 'Payment arranged / underwriter discretion' } : {});
      toast.success(action === 'approve' ? `Policy cancelled — notice sent for ${invoiceNumber}` : 'Queue entry dismissed');
      load();
      onChanged?.();
    } catch (e) {
      toast.error('Action failed');
    }
    setActingId(null);
  };

  const pending = data?.pending || [];
  if (!data) return null;

  return (
    <Card className="p-4 rounded-sm border bg-white" style={{ borderColor: pending.length ? 'rgba(226,75,74,0.35)' : 'rgba(136,150,168,0.2)' }}
      data-testid="cancellation-queue">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: pending.length ? C.red : C.navy }}>
        <ShieldOff className="w-3.5 h-3.5" />Cancellation Queue — non-payment
        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
          style={{ background: pending.length ? 'rgba(226,75,74,0.1)' : 'rgba(29,158,117,0.1)', color: pending.length ? C.red : C.green }}
          data-testid="cancellation-pending-count">{pending.length} pending</span>
      </h3>
      {pending.length === 0 ? (
        <p className="text-xs" style={{ color: C.slate }} data-testid="cancellation-queue-empty">
          No invoices awaiting cancellation. Final-notice invoices unpaid after the 7-day grace period land here automatically.
        </p>
      ) : (
        <div className="space-y-2">
          {pending.map(q => (
            <div key={q.queue_id} className="flex flex-wrap items-center gap-3 p-2.5 rounded-sm border"
              style={{ borderColor: 'rgba(226,75,74,0.2)', background: 'rgba(226,75,74,0.03)' }}
              data-testid={`cancellation-row-${q.invoice_number}`}>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold" style={{ color: C.navy }}>{q.business_name}</p>
                <p className="text-[10px] font-mono" style={{ color: C.slate }}>
                  {q.invoice_number} · {fmt$(q.amount_due)} due · {q.days_overdue}d overdue · broker {q.broker_name || '—'}
                </p>
              </div>
              <button onClick={() => act(q.queue_id, 'dismiss', q.invoice_number)} disabled={actingId === q.queue_id}
                data-testid={`dismiss-cancellation-${q.invoice_number}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-sm border text-[10px] font-semibold transition-colors hover:bg-slate-50 disabled:opacity-50"
                style={{ color: C.slate, borderColor: 'rgba(136,150,168,0.3)' }}>
                <XCircle className="w-3 h-3" />Dismiss
              </button>
              <button onClick={() => act(q.queue_id, 'approve', q.invoice_number)} disabled={actingId === q.queue_id}
                data-testid={`approve-cancellation-${q.invoice_number}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: C.red }}>
                {actingId === q.queue_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldOff className="w-3 h-3" />}
                Approve Cancellation
              </button>
            </div>
          ))}
        </div>
      )}
      {(data.processed || []).length > 0 && (
        <div className="mt-3 pt-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
          <p className="text-[9px] uppercase tracking-wider font-semibold mb-1" style={{ color: C.slate }}>Recently processed</p>
          {(data.processed || []).slice(0, 5).map(q => (
            <p key={q.queue_id} className="text-[10px] flex items-center gap-1.5" style={{ color: C.slate }}>
              {q.status === 'approved'
                ? <ShieldOff className="w-3 h-3" style={{ color: C.red }} />
                : <CheckCircle className="w-3 h-3" style={{ color: C.green }} />}
              {q.invoice_number} — {q.status === 'approved' ? `cancelled by ${q.resolved_by || 'staff'}` : q.status === 'resolved_paid' ? 'resolved by payment' : `dismissed by ${q.resolved_by || 'staff'}`}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
}
